
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { toast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import { addHeaderToPage } from '../pdf/header-utils';
import { addFooterToPage } from '../pdf/footer-utils';
import { renderTitle, renderMetadata, renderAnalysis } from '../pdf/content-utils';

export const usePDFExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { user, isSessionExpired, refreshSession } = useAuth();

  const exportToPDF = useCallback(async (
    drawingId: string,
    title: string,
    previewImage: string,
    analysisText: string | null,
    scenarioType: string | null
  ) => {
    console.log('PDF export requested:', { drawingId, title });

    // Validate authentication
    if (!user) {
      console.error('PDF export failed: No authenticated user');
      toast({
        variant: "destructive",
        title: "Export failed",
        description: "Please log in to export drawings",
      });
      return;
    }

    // Handle session expiration
    if (isSessionExpired) {
      console.log('Session expired, attempting refresh before export');
      try {
        const refreshed = await refreshSession();
        if (!refreshed) {
          toast({
            variant: "destructive",
            title: "Session expired",
            description: "Your session has expired. Please log in again.",
          });
          return;
        }
        console.log('Session refreshed successfully');
      } catch (error) {
        console.error('Session refresh error:', error);
        toast({
          variant: "destructive",
          title: "Authentication error",
          description: "Please try logging out and back in.",
        });
        return;
      }
    }

    setIsExporting(true);

    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        throw new Error('Could not fetch user profile');
      }

      const fullName = profile?.first_name && profile?.last_name 
        ? `${profile.first_name} ${profile.last_name}`
        : user.email || 'Anonymous User';

      const pdf = new jsPDF({ orientation: 'portrait' });
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const margin = 15;
      
      let yPosition = addHeaderToPage(pdf, pageWidth, margin);
      
      yPosition += renderTitle(pdf, title, yPosition, pageWidth, margin);

      const metadataWidth = (pageWidth - (margin * 3)) * 0.35;
      const imageWidth = (pageWidth - (margin * 3)) * 0.65;
      const imageHeight = imageWidth * 0.75;
      
      renderMetadata(pdf, fullName, scenarioType, yPosition, margin);

      const imageX = margin * 2 + metadataWidth;
      pdf.setFillColor(248, 248, 248);
      pdf.roundedRect(imageX - 2, yPosition - 2, imageWidth + 4, imageHeight + 4, 3, 3, 'F');
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(imageX, yPosition, imageWidth, imageHeight, 2, 2);
      pdf.addImage(previewImage, 'PNG', imageX, yPosition, imageWidth, imageHeight);

      yPosition += imageHeight + 25;

      if (analysisText) {
        yPosition = renderAnalysis(pdf, analysisText, yPosition, pageWidth, margin);
      }

      addFooterToPage(pdf, pageWidth, pageHeight, margin);

      pdf.save(`${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${drawingId}.pdf`);
      
      console.log('PDF export completed successfully');
      toast({
        title: "Export successful",
        description: "Your drawing has been exported as PDF",
      });
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        variant: "destructive",
        title: "Export failed",
        description: "There was an error exporting your drawing",
      });
    } finally {
      setIsExporting(false);
    }
  }, [user, isSessionExpired, refreshSession]);

  return {
    isExporting,
    exportToPDF
  };
};
